/* eslint-disable no-param-reassign */
import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';
import sharedMutations from 'vuex-shared-mutations';
import { isEmpty } from 'lodash';

import SessionHandler from './services/SessionHandler';
// eslint-disable-next-line import/no-cycle
import auth from './store/auth';
import sidebar from './store/sidebar';
import navigation from './store/navigation';
import paymentMethods from './store/paymentMethods';
import deliveryMethods from './store/deliveryMethods';
import countries from './store/countries';
import plaid from './store/plaid';
import captcha from './store/captcha';
import watchers from './store/watchers';
import { healthData } from './mixins/health';

const moment = require('moment');

Vue.use(Vuex);

const currency = require('./modules/currency.js');

const vuexPersist = new VuexPersist({
    key: 'kaoshi-money',
    storage: localStorage,
    // Exclude some modules from vuex persistence
    reducer: (state) => {
        const {
            auth: _auth,
            sidebar: _sidebar,
            navigation: _navigation,
            paymentMethods: _paymentMethods,
            deliveryMethods: _deliverMethods,
            captcha: _captcha,
            ...others
        } = state;
        return others;
    },
});

export default new Vuex.Store({
    state: {
        menuIsClosed: true,
        pageHeaderClass: 'request',
        pageHeaderTitle: 'Loading...',
        closeButtonIsVisible: false,
        mobileMenuIsVisible: true,
        hideLogoPart: false,
        isExpiring: false,
        logoPartClasses: [],
        preferences: {
            filters: {
                toCurrencyCode: '',
                fromCurrencyCode: '',
                fromCountry: {},
                toCountry: {},
                amount: null,
                poster_payment_method_type: [],
                matcher_payment_method_type: [],
                duration_object: [],
                type: [],
                is_partial_match_allowed: [],
            },
        },
        defaultRateAlertId: '',
        user: {},
        testPaymentEnabled: false,
        emailNotification: null,
        unreadMessagesCount: 0,
        handlingSession: false,
        /*
         * -1: the session popup has not been shown yet
         * 0: the session popup has been shown
         * 1: the session popup is shown
         */
        sessionPopup: -1,
        storeCreationTime: `Store created at ${moment().format()}`,
        currentLoginTime: moment(),
        userSettings: {},
        isShowP2PFirst: false,
        shoppingList: [],
        autoloans: [],
        healthData,
        activeMatchesCount: 0,
        activeRequestsCount: 0,
        orderInformationDialog: true,
        transferInformationDialog: true,
        transferInformationWarning: true,
        hideTransferInstructions: false,
        WREmailUpdateConfirmation: true,
        hidePartialMatchDisclaimer: false,
        hideMatchInformationDialog: false,
        ribbon: null,
        unregisteredPaymentRequest: null,
        onboarding: false,
    },
    actions: getActions(),
    mutations: getMutations(),
    getters: {
        isUserAuthenticated: ({ authToken }) => !!authToken && !!authToken.access_token,
        userSettings: ({ userSettings }) => {
            if (isEmpty(userSettings)) {
                return {
                    show_name: true,
                    show_avatar: true,
                };
            }

            return userSettings;
        },
        userCountry: (state, getters, rootState, rootGetters) => {
            const kaoshiCountries = rootGetters['countries/kaoshiCountries'];
            const countryAbbreviation = state.user.country_abbreviation;
            return kaoshiCountries.find((country) => country.abbreviation === countryAbbreviation);
        },
        preferences: (state) => {
            const fromCurrency = currency.findCurrency(state.preferences.filters.fromCurrencyCode);
            const toCurrency = currency.findCurrency(state.preferences.filters.toCurrencyCode);

            const hasValidCurrencyCodes = Object.keys(fromCurrency).length > 0 && Object.keys((toCurrency).length > 0);
            const posterCurrencyIsValid = hasValidCurrencyCodes && fromCurrency.is_poster_from_currency;
            const matcherCurrencyIsValid = hasValidCurrencyCodes && fromCurrency.is_matcher_from_currency;

            if (!hasValidCurrencyCodes) {
                setDefaultCurrencies();
            } else if (!posterCurrencyIsValid && state.user.can_post_offers && !state.user.can_accept_offers) {
                setDefaultCurrencies();
            } else if (!matcherCurrencyIsValid && !state.user.can_post_offers && state.user.can_accept_offers) {
                setDefaultCurrencies();
            }

            function setDefaultCurrencies() {
                if (state.user.can_post_offers) {
                    state.preferences.filters.fromCurrencyCode = 'NGN';
                    state.preferences.filters.toCurrencyCode = 'USD';
                } else {
                    state.preferences.filters.fromCurrencyCode = 'USD';
                    state.preferences.filters.toCurrencyCode = 'NGN';
                }
            }
            return state.preferences;
        },
    },
    plugins: [
        vuexPersist.plugin,
        sharedMutations({ predicate: ['storeUser', 'storeAuthToken', 'setCurrentLoginTime', 'setIsShowP2PFirst'] }),
        watchers,
    ],
    modules: {
        auth,
        sidebar,
        navigation,
        paymentMethods,
        deliveryMethods,
        countries,
        plaid,
        captcha,
    },
});

function getActions() {
    return {
        refreshToken: SessionHandler.refreshToken,
        showSessionDialog: SessionHandler.showSessionDialog,
        startSessionCheckTimer: SessionHandler.startSessionCheckTimer,
        updateOrderInformationDialog({ commit }, payload) {
            commit('storeOrderInformationOnTransfer', payload);
        },
        updateTransferInformationDialog({ commit }, payload) {
            commit('storeTransferInformationDialog', payload);
        },
        updateInformationWarning({ commit }, payload) {
            commit('storeInformationWarning', payload);
        },
        updateTransferInstructions({ commit }, payload) {
            commit('setTransferInstructionsStatus', payload);
        },
        updateWREmailUpdateConfirmation({ commit }, payload) {
            commit('storeWREmailUpdateConfirmation', payload);
        },
        updateHidePartialMatchDisclaimer({ commit }, payload) {
            commit('setHidePartialMatchDisclaimer', payload);
        },
        updateMatchInformationDialog({ commit }, payload) {
            commit('setMatchInformationDialog', payload);
        },
        updateRibbon({ commit }, payload) {
            commit('setRibbon', payload);
        },
        updateUnregisteredPaymentRequest({ commit }, payload) {
            commit('setUnregisteredPaymentRequest', payload);
        },
        async loadUser({ state, commit }) {
            try {
                const { data } = await Vue.backendApi()
                    .user(state.user.id)
                    .getUser();

                commit('storeUser', {
                    ...state.user,
                    ...data,
                });
            } catch (error) {
                Vue.$swal({
                    type: 'error',
                    title: 'Error loading user settings',
                    text: error,
                });
            }
        },
    };
}

/**
 * Get the mutations.
 */
function getMutations() {
    return {
        addLogoPartClass,

        clearLogoPartClass,
        closeButtonIsVisible,

        defaultRateAlertId,

        hideLogoPart,

        menuIsClosed,
        mobileMenuIsVisible,

        pageHeaderClass,
        pageHeaderTitle,
        preferences,
        setIsShowP2PFirst,

        removeLogoPartClass,

        storeUser,
        storeUserEmailNotification,
        storeUnreadMessagesCount,
        storeAuthToken,
        storeActiveMatchesCount,
        storeActiveRequestsCount,

        setCurrentLoginTime,

        setHandlingSession,
        setSessionCheckId,
        setSessionPopup,
        loadUserSettings,
        addToCart,
        removeFromCart,
        removeAll,
        saveToHealthData,
        addToAutoLoan,
        removeAutoLoan,
        removeLoans,
        storeOrderInformationOnTransfer,
        storeTransferInformationDialog,
        storeInformationWarning,
        setTransferInstructionsStatus,
        storeWREmailUpdateConfirmation,
        storeTestPaymentStatus,
        setHidePartialMatchDisclaimer,
        setMatchInformationDialog,
        setRibbon,
        setUnregisteredPaymentRequest,
        setOnboarding(state, value) {
            state.onboarding = value;
        },
    };

    function storeTestPaymentStatus(state, value) {
        state.testPaymentEnabled = value;
    }

    /**
     * Set the session check ID.
     *
     * This is a value returned by set interval.
     *
     * @param state
     * @param value
     */
    function setSessionCheckId(state, value) {
        state.sessionCheckId = value;
    }

    /**
     * Set the handling session variable.
     *
     * @param state
     * @param value
     */
    function setHandlingSession(state, value) {
        state.handlingSession = !!value;
    }

    /**
     * Set the value of the session popup counter.
     *

     ss* @param state
     * @param value
     */
    function setSessionPopup(state, value) {
        state.sessionPopup = value;
    }

    /**
     * Set the current login time; uses "now" if no value set.
     *
     * @param state
     * @param value
     */
    function setCurrentLoginTime(state, value) {
        state.currentLoginTime = value || moment();
    }

    function storeUser(state, payload) {
        if (payload && Vue.$gtag) Vue.$gtag.set({ user_id: payload.id });
        Vue.set(state, 'user', payload);
    }

    function storeAuthToken(state, payload) {
        Vue.set(state, 'authToken', payload);
    }

    function menuIsClosed(state, payload) {
        state.menuIsClosed = payload;
    }

    function pageHeaderClass(state, payload) {
        state.pageHeaderClass = payload;
    }

    function pageHeaderTitle(state, payload) {
        state.pageHeaderTitle = payload;
    }

    function mobileMenuIsVisible(state, payload) {
        state.mobileMenuIsVisible = payload;
    }

    function closeButtonIsVisible(state, payload) {
        state.closeButtonIsVisible = payload;
    }

    function hideLogoPart(state, payload) {
        state.hideLogoPart = payload;
    }

    function addLogoPartClass(state, payload) {
        state.logoPartClasses.push(payload);
    }

    function removeLogoPartClass(state, payload) {
        const index = state.logoPartClasses.findIndex((n) => n === payload);
        state.logoPartClasses.splice(index, 1);
    }

    function clearLogoPartClass(state, _payload) {
        state.logoPartClasses.splice(0, state.logoPartClasses.length);
    }

    function preferences(state, payload) {
        Vue.set(state, 'preferences', payload);
    }

    function setIsShowP2PFirst(state, payload) {
        Vue.set(state, 'isShowP2PFirst', payload);
    }

    function defaultRateAlertId(state, payload) {
        Vue.set(state, 'defaultRateAlertId', payload);
    }

    function loadUserSettings(state, payload) {
        Vue.set(state, 'userSettings', payload);
    }

    function addToCart(state, data) {
        state.shoppingList.push(data);
    }

    function removeFromCart(state, data) {
        state.shoppingList = state.shoppingList.filter((key) => key.id !== data.id && key.brand !== data.brand);
    }

    function removeAll(state, _data) {
        state.shoppingList = [];
    }

    function saveToHealthData(state, data) {
        state.healthData = [...state.healthData, data];
    }

    function addToAutoLoan(state, data) {
        state.autoloans.push(data);
    }

    function removeAutoLoan(state, data) {
        state.autoloans = state.autoloans.filter((key) => key.id !== data.id && key.brand !== data.brand);
    }

    function removeLoans(state, _data) {
        state.autoloans = [];
    }

    function storeUserEmailNotification(state, notification) {
        Vue.set(state, 'emailNotification', notification);
    }

    function storeUnreadMessagesCount(state, unreadMessagesCount) {
        Vue.set(state, 'unreadMessagesCount', unreadMessagesCount);
    }

    function storeActiveMatchesCount(state, activeMatchesCount) {
        Vue.set(state, 'activeMatchesCount', activeMatchesCount);
    }

    function storeActiveRequestsCount(state, activeRequestsCount) {
        Vue.set(state, 'activeRequestsCount', activeRequestsCount);
    }

    function storeOrderInformationOnTransfer(state, payload) {
        Vue.set(state, 'orderInformationDialog', payload);
    }

    function storeTransferInformationDialog(state, payload) {
        Vue.set(state, 'transferInformationDialog', payload);
    }

    function storeInformationWarning(state, payload) {
        Vue.set(state, 'transferInformationWarning', payload);
    }

    function setTransferInstructionsStatus(state, payload) {
        Vue.set(state, 'hideTransferInstructions', payload);
    }

    function storeWREmailUpdateConfirmation(state, payload) {
        Vue.set(state, 'WREmailUpdateConfirmation', payload);
    }

    function setHidePartialMatchDisclaimer(state, payload) {
        Vue.set(state, 'hidePartialMatchDisclaimer', payload);
    }

    function setMatchInformationDialog(state, payload) {
        Vue.set(state, 'hideMatchInformationDialog', payload);
    }

    function setRibbon(state, payload) {
        Vue.set(state, 'ribbon', payload);
    }

    function setUnregisteredPaymentRequest(state, payload) {
        Vue.set(state, 'unregisteredPaymentRequest', payload);
    }
}
